import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import { ContentProcessingStatusType } from "@/admin/entities/contents";
import type { ContentRow } from "./types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { Stack, Typography } from "@mui/material";

/**
 * Конфигурация UI для feature управления контентом
 */

// Конфигурация статусов обработки (цвета/подписи как в partner-карточке)
const statusChipConfig: Record<
  ContentProcessingStatusType,
  { label: string; color: ChipProps["color"] }
> = {
  [ContentProcessingStatusType.NofileType]: {
    label: "Нет файла",
    color: "default",
  },
  [ContentProcessingStatusType.ProcessingType]: {
    label: "Обработка",
    color: "info",
  },
  [ContentProcessingStatusType.CompleteType]: {
    label: "Готово",
    color: "success",
  },
  [ContentProcessingStatusType.ErrorType]: {
    label: "Ошибка",
    color: "error",
  },
};

// Конфигурация колонок таблицы
export const getColumnsConfig = (): ColumnDefinition<ContentRow>[] => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "preview",
    label: "Превью",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const preview = data as ContentRow["preview"];
        if (!preview) return null;
        return (
          <img
            src={preview.url}
            alt=""
            style={{ maxWidth: 96, borderRadius: 4 }}
          />
        );
      },
    },
  },
  {
    name: "title",
    label: "Название",
    options: {
      customBodyRender: (data: unknown) => {
        const content = data as ContentRow;
        return (
          <CrudLink id={content.id} tab="details">
            {content.title}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "platform",
    label: "Платформа",
    options: {
      customBodyRender: (data: unknown) => {
        const platform = data as ContentRow["platform"];
        if (!platform) {
          return <span style={{ color: "#999" }}>Глобальный</span>;
        }
        return platform.title;
      },
    },
  },
  {
    name: "processingStatus",
    label: "Статус обработки",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => {
        const status = value as ContentProcessingStatusType;
        const { label, color } = statusChipConfig[status] ?? {
          label: status,
          color: "default" as const,
        };
        return <Chip label={label} color={color} size="small" />;
      },
    },
  },
  {
    name: "publishAt",
    label: "Опубликовано",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => {
        const dateValue = value as string | null;
        return dateValue ? <CustomCellDate date={dateValue} /> : "—";
      },
    },
  },
  {
    name: "createdAt",
    label: "Создано",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
  {
    name: "deletedAt",
    label: "Удалено",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => {
        const dateValue = value as string | null;
        return dateValue ? <CustomCellDate date={dateValue} /> : "—";
      },
    },
  },
];

// Конфигурация текстов интерфейса
export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Контент",
  refetchList: "Обновить список",
  deleteItems: "Удалить контент",
  itemsDeleted: "Контент удалён",
  restoreItems: "Восстановить контент",
  itemsRestored: "Контент восстановлен",
  cancel: "Отмена",
  editItem: "Контент",
  showTitle: "Детали",
});

// Колонки для мобильного представления: превью с названием и статус обработки
export const getMobileColumnsConfig = (): DataListViewColumns<ContentRow> => ({
  main: {
    title: "Контент",
    sx: { flex: 1, minWidth: 0 },
    renderCell: (row) => (
      <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
        {row.preview && (
          <img
            src={row.preview.url}
            alt=""
            style={{
              width: 64,
              height: 36,
              objectFit: "cover",
              borderRadius: 4,
            }}
          />
        )}
        <Stack sx={{ minWidth: 0 }}>
          <CrudLink id={row.id} tab="details" noWrap>
            {row.title}
          </CrudLink>
          <Typography variant="body2" color="textSecondary" noWrap>
            {row.platform?.title ?? "Глобальный"}
          </Typography>
        </Stack>
      </Stack>
    ),
  },
  processingStatus: {
    title: "Обработка",
    sx: { width: 108, flexShrink: 0 },
    renderCell: (row) => {
      const status = row.processingStatus as ContentProcessingStatusType;
      const { label, color } = statusChipConfig[status] ?? {
        label: status,
        color: "default" as const,
      };
      return <Chip label={label} color={color} size="small" />;
    },
  },
});
